import { Row, Col, message, Table, Pagination, Image, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilterButton, AppFilter } from "components/AppFilter";
import { getAdminUsers } from "../../store/admin-slice/adminServicesSlice";
import { IAdminUsers } from "../../common/interface/IAdminUsers";
import { AppPartnerUserFilter } from "../../components/AppFilter/AppPartnerUserFilter/AppPartnerUserFilter";

type PartialUserType = Pick<IAdminUsers, "id" | "firstname" | "lastname" | "phone"> & {
  key: string;
};

const MESSAGE_KEY = "user_checkout_qr";
const QR_SIZE = 300;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const StyledQRThumbnail = styled(Image)`
  border-radius: 50% !important;
  object-fit: cover;
  cursor: pointer;
`;

const DeliveryDateLabel = styled(Typography.Text)`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 0.75rem;
  display: block;
`;

const getDeliveryDate = (): string => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const generateQRCodes = async (
  users: IAdminUsers[],
  deliveryDate: string
): Promise<Map<number, string>> => {
  const entries = await Promise.all(
    users.map((user) =>
      QRCode.toDataURL(
        JSON.stringify({ userId: String(user.id), deliveryDate }),
        { margin: 1, width: QR_SIZE, color: { dark: "#000000", light: "#ffffff" } }
      ).then((url) => [user.id, url] as [number, string])
    )
  );
  return new Map(entries);
};

const UserCheckoutQRPage = () => {
  useDocumentTitle("User Checkout QR");
  const dispatch = useAppDispatch();
  const { adminUsers, status, error } = useAppSelector(
    (state) => state.adminServices
  );
  const [filterState, setFilterState] = useState(false);
  const [searchParams] = useSearchParams();
  const [filterCount, setFilterCount] = useState(0);
  const [qrCodeMap, setQrCodeMap] = useState<Map<number, string>>(new Map());
  const [qrLoading, setQrLoading] = useState(false);

  // Stable today's date — computed once per mount, won't shift mid-session
  const deliveryDate = useMemo(getDeliveryDate, []);

  const getData = useCallback(
    (pageNum = 1, pageSize = 25, filter = "") => {
      dispatch(getAdminUsers({ pageNo: pageNum, pageSize, filter }));
    },
    [dispatch]
  );

  // Fetch data whenever search params change
  useEffect(() => {
    const filter = searchParams.toString();
    setFilterCount(
      Object.keys(Object.fromEntries(new URLSearchParams(filter))).length
    );
    getData(1, 25, filter);
  }, [getData, searchParams]);

  // Show API status messages
  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading Users", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({ content: "Users Loaded.", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }
    return () => { message.destroy(); };
  }, [status, error]);

  // Generate QR codes whenever the user list changes (search / pagination)
  useEffect(() => {
    if (!adminUsers.data.length) {
      setQrCodeMap(new Map());
      return;
    }

    let cancelled = false;
    setQrLoading(true);

    generateQRCodes(adminUsers.data, deliveryDate)
      .then((map) => {
        if (!cancelled) {
          setQrCodeMap(map);
          setQrLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setQrLoading(false);
      });

    return () => { cancelled = true; };
  }, [adminUsers.data, deliveryDate]);

  // Rows — derived from API data
  const rows = useMemo<PartialUserType[]>(
    () =>
      adminUsers.data.map((au, i) => ({
        id: au.id,
        firstname: au.firstname,
        lastname: au.lastname,
        phone: au.phone,
        key: `user-qr-${i}`,
      })),
    [adminUsers.data]
  );

  // Columns — rebuilt only when qrCodeMap reference changes
  const columns = useMemo<ColumnsType<PartialUserType>>(
    () => [
      { key: "id", title: "User-Id", dataIndex: "id", width: 80 },
      { key: "firstname", title: "First Name", dataIndex: "firstname", width: 150 },
      { key: "lastname", title: "Last Name", dataIndex: "lastname", width: 150 },
      { key: "phone", title: "Phone", dataIndex: "phone", width: 150 },
      {
        key: "checkoutQR",
        title: "Checkout QR",
        width: 100,
        align: "center" as const,
        render: (_: unknown, record: PartialUserType) => {
          const qrUrl = qrCodeMap.get(record.id);
          if (!qrUrl) return null;
          return (
            <Image.PreviewGroup>
              <StyledQRThumbnail
                src={qrUrl}
                width={48}
                height={48}
                preview={{ src: qrUrl }}
              />
            </Image.PreviewGroup>
          );
        },
      },
    ],
    [qrCodeMap]
  );

  const isLoading = status === "loading" || qrLoading;

  const Paginator = () => {
    const { pageNo, pageSize, total } = adminUsers;
    if (!total) return null;
    return (
      <StyledPagination
        current={pageNo}
        pageSize={pageSize}
        total={total}
        onChange={(page, size) => getData(page, size, searchParams.toString())}
      />
    );
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="User Checkout QR" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState(!filterState)}
              filterCount={filterCount}
            />
          </div>
        </AppActionStrip>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppPartnerUserFilter />
        </AppFilter>
      </Row>
      <Row>
        <Col xs={24}>
          <DeliveryDateLabel strong>
            Delivery Date: {deliveryDate}
          </DeliveryDateLabel>
        </Col>
      </Row>
      {rows.length > 0 || isLoading ? (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={isLoading}
              bordered
            />
          </Col>
          <Col xs={24}>
            <Paginator />
          </Col>
        </Row>
      ) : (
        <>No users to show.</>
      )}
    </>
  );
};

export default UserCheckoutQRPage;
